/**
 * One physical wall, two bodies — and the opening that has to pierce both.
 *
 * RoomPlan's merge routinely emits a pair of structure walls a few centimetres
 * apart describing ONE wall, and the double-height raise makes more of them by
 * extending a storey-0 wall up through the storey-1 wall sharing its plane. An
 * opening is parented to exactly one of the pair, so the other stays solid.
 *
 * `PolygonWalls` has carried a geometric bridge for this since `A1A92296`'s
 * stairwell window `DC23B903` went missing from outside. `StructureWallBoxes`
 * — which CSG-subtracts the same openings out of the wall SOLIDS — never got
 * one, so it kept matching on `parentWallIds` alone. On `19A7B370` that left
 * the storey-0 wall `BA7C32CF` rendered as an uncut box directly across the
 * arrival door `3BDB4334` (parented to `C4050D60`, 12 cm away): the faces were
 * carved, the solid was not, and the head of the stairs was walled off.
 *
 * Both renderers now ask this module, so they cannot drift apart again.
 *
 * Deliberately NOT fixed by de-duplicating the walls upstream: that was built
 * and reverted, because deleting a body costs the room on its side a face and
 * the trace fills the gap with slivers. See
 * claude-docs/2026-08-12-coincident-wall-dedup-plan.md.
 */

import {
  elevatedSillLift,
  openingVerticalSpan,
} from '../../../utils/wallGeometryUtils';
import { alongWallSpan } from './wallFaceCarve';

// How close an opening's centre must lie to a wall's plane before that wall is
// treated as one the opening physically passes through. On A1A92296 the
// coincident pairs sit 0.034-0.164 m apart. 0.20 m clears all of them while
// staying well under the 0.29 m that separates walls known to be genuinely
// different (BE8E1430's Study/Laundry wall and the livingRoom wall beside it).
export const COINCIDENT_PLANE_TOL_M = 0.2;

// Reaching across to a DIFFERENT structure wall demands more than the overlap
// the same-wall bridge needs: the opening must be essentially CONTAINED in that
// wall, i.e. it really is an opening in it rather than one that merely grazes
// it. On A1A92296 window `DC23B903` sits entirely inside the storey-1 wall
// `4C5948B6` (100%) and must be carved, while landing door `3A4E3CF3` only
// poked its head 0.568 m of 2.134 m past that wall's floor line (27%) — cutting
// that produced a slot into the KITCHEN at floor level, with the door's far
// face visible through it.
export const COINCIDENT_CONTAINMENT_MIN = 0.8;

/**
 * A wall face's plan line: origin, unit direction and length, taken from the
 * two most separated XZ vertices. Every vertex of a face lies on the wall's
 * line, so this is exact for stepped and raked faces too.
 */
export function wallPlanLine(corners) {
  if (!corners || corners.length < 2) return null;
  let a = corners[0];
  let b = corners[0];
  let best = -1;
  for (let i = 0; i < corners.length; i++) {
    for (let j = i + 1; j < corners.length; j++) {
      const d = Math.hypot(
        corners[j].x - corners[i].x,
        corners[j].z - corners[i].z
      );
      if (d > best) {
        best = d;
        a = corners[i];
        b = corners[j];
      }
    }
  }
  if (!(best > 1e-6)) return null;
  return {
    origin: { x: a.x, z: a.z },
    u: { x: (b.x - a.x) / best, z: (b.z - a.z) / best },
    length: best,
  };
}

/** Perpendicular distance from an XZ point to a plan line. */
export function perpDistanceToLine(point, line) {
  const dx = point.x - line.origin.x;
  const dz = point.z - line.origin.z;
  const along = dx * line.u.x + dz * line.u.z;
  return Math.hypot(dx - along * line.u.x, dz - along * line.u.z);
}

/**
 * A wall SOLID's plan line and Y extent, from `polygonCorners` when it has
 * them (exact for a stepped or raked top) and from centre/dimensions/yaw
 * otherwise. `dimensions.x` is the along-wall length; local +x rotated by the
 * yaw is the run direction.
 */
/**
 * Does this opening lie in the plane of the face described by `line`?
 *
 * Both of `PolygonWalls`' bridges ask this, for the same reason and with the
 * same tolerance: an opening is a hole in the bodies occupying ITS OWN plane,
 * and in no others.
 *
 * It is the id bridge that needs saying out loud. That one reaches every
 * surface carrying a structure wall's id, on the assumption that they are the
 * wall's two PARALLEL faces. A wall's END CAP is not one of those: it is the
 * return across the wall's thickness at the wall's end, perpendicular to the
 * faces, and it carries the same `alignment.structureWallId` because it
 * genuinely is part of that wall. Bridged onto a cap, `alongWallSpan` projects
 * an opening's DEPTH onto the cap's own axis — which is exactly the cap's
 * width — so an opening metres away along the wall reads as spanning the cap
 * edge to edge.
 *
 * On `19A7B370`'s living room that consumed both 11.43 cm end-cap faces of
 * `CDD96679` whole: slider `F77E4577` is 3.34 m long and sits 0.198 m short of
 * the caps, but projected onto their axis it covered all 0.1143 m of them, and
 * cutting to the floor plus the styled door's 8 cm head buffer (2.3119 m)
 * cleared their 2.2619 m tops. Nothing survived the carve.
 *
 * The distances say it cleanly. A wall's two real faces are half a thickness
 * off its plane — 0.057 m there — while the caps sat 1.87 m away.
 *
 * Permissive when the data to judge on is missing, like the vertical guards
 * beside it: this narrows a bridge, it is not a licence to drop an opening.
 */
export function openingInWallPlane(opening, line) {
  if (!line || !opening?.center) return true;
  return perpDistanceToLine(opening.center, line) <= COINCIDENT_PLANE_TOL_M;
}

export function wallSolidGeometry(wall) {
  const pc = wall?.polygonCorners;
  if (Array.isArray(pc) && pc.length >= 2) {
    const line = wallPlanLine(pc);
    const ys = pc.map((c) => c.y);
    if (line) return { line, bottom: Math.min(...ys), top: Math.max(...ys) };
  }
  const c = wall?.center;
  const d = wall?.dimensions;
  if (!c || !d) return null;
  const ry = wall.rotation?.y || 0;
  const u = { x: Math.cos(ry), z: -Math.sin(ry) };
  const half = (d.x || 0) / 2;
  if (!(half > 1e-6)) return null;
  return {
    line: {
      origin: { x: c.x - u.x * half, z: c.z - u.z * half },
      u,
      length: d.x,
    },
    bottom: c.y - (d.y || 0) / 2,
    top: c.y + (d.y || 0) / 2,
  };
}

/**
 * Does `opening` physically pass through the wall body described by `geom`
 * (`{ line, bottom, top }`), even though it is parented elsewhere?
 *
 * Three gates, all corpus-tuned in `PolygonWalls`: its centre lies on this
 * wall's plane, it is essentially CONTAINED in the wall vertically, and its
 * footprint overlaps the wall's span. Carving is safe by construction — both
 * callers run a real boolean, so an opening that only partly covers the body
 * becomes a notch rather than breaking it.
 */
export function openingPiercesWall(opening, geom) {
  if (!opening?.center || !opening?.dimensions || !geom?.line) return false;

  const h = opening.dimensions.y;
  const cy = opening.center.y;
  if (typeof cy === 'number' && typeof h === 'number' && h > 0) {
    const overlap =
      Math.min(cy + h / 2, geom.top) - Math.max(cy - h / 2, geom.bottom);
    if (overlap < h * COINCIDENT_CONTAINMENT_MIN) return false;
  }

  if (perpDistanceToLine(opening.center, geom.line) > COINCIDENT_PLANE_TOL_M) {
    return false;
  }

  const span = alongWallSpan(
    {
      center: opening.center,
      dimensions: opening.dimensions,
      rotation: opening.rotation,
    },
    geom.line.u,
    geom.line.origin
  );
  return span.max > 0 && span.min < geom.line.length;
}

/**
 * Does this opening cut DOWN TO THE FLOOR of the face being carved?
 *
 * A door (and a demoed span, which is a full-height removal you see the floor
 * through) is cut from the face's own bottom edge rather than from its scanned
 * sill: RoomPlan does not reliably put `center.y` at `floorY + height/2`, so
 * honouring the sill directly leaves a threshold-height bar across the doorway.
 *
 * That reasoning holds only for the opening's OWN wall, where the face's bottom
 * IS its floor line. A body reached through the coplanar bridge is a different
 * wall that merely shares the plane, and it can run to a different storey: on
 * `19A7B370` the arrival door sits 2.899 m up the double-height `BA7C32CF`,
 * whose face bottoms out on the GROUND floor. Snapping there cut the doorway at
 * 0-2.112 while the door stood at 2.899-5.011, so the head of the stairs
 * rendered solid with a doorway punched into the storey below it. Such a face
 * needs the door-shaped hole where the door actually is, and nothing more.
 *
 * And it holds only while the sill is UNRELIABLE, which is what
 * `sillAboveFaceFloor` decides. The unreliability the snap exists for is a
 * threshold's worth; a sill a REAL STEP above the face's floor was put there by
 * a pipeline pass (a stair landing, a cluster step, a reconstructed half-level)
 * and is the most reliable thing about the opening. Snapping such a door to the
 * floor cuts its hole a step below where the leaf stands — `Door.jsx` seats a
 * leaf by the same test — which renders as a doorway open under the door and the
 * head of the door buried in the wall (#2286, seen on `7657745E`: hole
 * 1.479-3.716 against a leaf at 2.416-4.653).
 *
 * @param {number} [sillAboveFaceFloor] - the opening's sill minus the face's own
 *   lowest point, in the face's frame. Omitted → treated as level with it.
 * @param {Object} [opening] - the canonical opening: one kept at a small
 *   measured step (`_sillAtStep`) is followed from a smaller lift
 *   (`elevatedSillLift`).
 */
export function cutsDownToFloor({
  type,
  isDemoed,
  reachedByCoplanarBridge,
  sillAboveFaceFloor = 0,
  opening,
}) {
  if (reachedByCoplanarBridge) return false;
  if (!(type === 'door' || !!isDemoed)) return false;
  return !(sillAboveFaceFloor > elevatedSillLift(opening));
}

/**
 * Vertical extent of the hole an opening cuts in one face, in that face's own
 * 2D frame. `centerV` is the opening centre projected onto the face's up axis.
 *
 * `topBuffer` is headroom for a styled door's frame, and it goes on the TOP —
 * which is what it was always for. It used to be folded into `openingHeight` by
 * the caller, so on the floor-snapped path it did reach only the top, but on the
 * sill path it split in two and pushed the BOTTOM down by half of it. That path
 * was unreachable for doors while every door snapped to the floor; an elevated
 * one takes it, and would have opened a 4 cm gap under its own leaf.
 */
export function openingHoleSpanY({
  cutsToFloor,
  faceMinY,
  centerV,
  openingHeight,
  topBuffer = 0,
}) {
  // The face's own frame, handed to the shared decision as an opening: `centerV`
  // is the centre on the face's up axis and `faceMinY` its bottom, which is what
  // `openingVerticalSpan` means by a centre and a surface floor. `cutsToFloor` is
  // already the caller's type reading (`cutsDownToFloor`), sill included, so the
  // span is asked not to re-apply it.
  const { bottom } = openingVerticalSpan(
    { center: { y: centerV }, dimensions: { y: openingHeight } },
    faceMinY,
    { snapsToFloor: cutsToFloor }
  );
  return { bottom, top: bottom + openingHeight + topBuffer };
}
